import { type ReactElement, type ReactNode } from 'react'
import cx from 'classnames'
import styles from './AnswerOptions.module.css'
import {
  GridList,
  GridListItem,
  type Key,
  type Selection,
} from 'react-aria-components'
import { Icon, type IconName } from '@/components/Icon'

export interface AnswerOptionsProps<T extends object> {
  mode?: 'single' | 'multi'
  layout?: 'stack' | 'grid'
  children?: ReactNode | ((item: T) => ReactElement)
  items?: T[]
  selectedKeys?: Selection
  disabledKeys?: Iterable<Key>
  disallowEmptySelection?: boolean
  onSelectionChange?: (selection: Selection) => unknown
  'aria-label'?: string
  'aria-labelledby'?: string
  /**
   * Test ID attribute for the grid list.
   */
  testId?: string
}

/**
 * Group of answer options for single-selection.
 */
export function AnswerOptions<T extends object>({
  children,
  mode = 'single',
  layout = 'stack',
  testId,
  ...rest
}: AnswerOptionsProps<T>) {
  return (
    <GridList
      className={cx(styles.root, layout === 'grid' && styles.grid)}
      selectionMode={mode === 'single' ? 'single' : 'multiple'}
      selectionBehavior="toggle"
      layout={layout === 'grid' ? 'grid' : 'stack'}
      data-test={testId}
      {...rest}
    >
      {children}
    </GridList>
  )
}

interface AnswerOptionProps {
  label: string
  description?: ReactNode
  isDisabled?: boolean
  decoration?: ReactNode
  id?: string
  withoutSelectionIcon?: boolean
  /**
   * Test ID attribute for the option item.
   */
  testId?: string
  onPress?: () => void
}

/**
 * An answer option.
 */
export function AnswerOption({
  isDisabled,
  label,
  description,
  decoration,
  id,
  withoutSelectionIcon,
  testId,
  onPress,
}: AnswerOptionProps) {
  return (
    <GridListItem
      onPress={onPress}
      textValue={label}
      isDisabled={isDisabled}
      id={id}
      data-test={testId}
      className={({
        isSelected,
        isFocusVisible,
        isHovered,
        isPressed,
        isDisabled,
      }) =>
        cx(
          styles.option,
          isFocusVisible && styles.focusVisible,
          isHovered && styles.hover,
          isPressed && styles.pressed,
          isDisabled && styles.disabled,
          isSelected && styles.selected,
        )
      }
    >
      {({ selectionMode, isSelected }) => {
        const iconName: IconName =
          selectionMode === 'single'
            ? isSelected
              ? 'radio-check'
              : 'radio-default'
            : isSelected
              ? 'checkbox-ticked'
              : 'checkbox-default'
        return (
          <>
            {decoration && (
              <div className={styles.decoration}>{decoration}</div>
            )}
            <div className={styles.text}>
              <div className={styles.label}>{label}</div>
              {description && (
                <div className={styles.description}>{description}</div>
              )}
            </div>
            {!withoutSelectionIcon && (
              <div className={styles.icon}>
                <Icon name={iconName} />
              </div>
            )}
          </>
        )
      }}
    </GridListItem>
  )
}

/**
 * Decoration representing a default user. For use with {@link AnswerOption}.
 */
export function DefaultUserAnswerOptionDecoration() {
  return (
    <div className={styles.defaultUserAnswerOptionDecoration}>
      <Icon name="user" />
    </div>
  )
}

interface AnswerOptionIconDecorationProps {
  name: IconName
}

/**
 * Answer Option Icon Decoration
 */
export function AnswerOptionIconDecoration({
  name,
}: AnswerOptionIconDecorationProps) {
  return (
    <div className={styles.answerOptionIconDecoration}>
      <Icon name={name} />
    </div>
  )
}
